import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PscCreateRequest,
  PscUpdateRequest,
  PscListQuery,
  PscListResponse,
  PscResponse,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<PscListQuery>) => ['psc-documents', 'list', q] as const,
  one: (id: string) => ['psc-documents', 'one', id] as const,
};

function toQueryString(q: Partial<PscListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.status) params.set('status', q.status);
  if (q.periodId) params.set('periodId', q.periodId);
  if (q.search) params.set('search', q.search);
  if (q.workDateFrom) params.set('workDateFrom', new Date(q.workDateFrom).toISOString());
  if (q.workDateTo) params.set('workDateTo', new Date(q.workDateTo).toISOString());
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function usePscList(query: Partial<PscListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<PscListResponse>(`/api/documents/psc${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function usePsc(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['psc-documents', 'one', 'NIL'],
    queryFn: () => api<PscResponse>(`/api/documents/psc/${id}`),
    enabled: !!id,
  });
}

export function useCreatePsc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PscCreateRequest) =>
      api<PscResponse>('/api/documents/psc', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['psc-documents', 'list'] });
    },
  });
}

export function useUpdatePsc(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PscUpdateRequest) =>
      api<PscResponse>(`/api/documents/psc/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['psc-documents', 'list'] });
    },
  });
}

export function useClosePsc(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<PscResponse>(`/api/documents/psc/${id}/close`, { method: 'POST', body: {} }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['psc-documents', 'list'] });
    },
  });
}

export function useDeletePsc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/documents/psc/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['psc-documents', 'list'] });
    },
  });
}
